import { getPartByComponentType } from "@/pages/configurations/partComponentTypes/repositories/PartComponentTypeRepository";
import { Button, CircularProgress, Grid, Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { ComponentType } from "@/pages/configurations/componentTypes/models/ComponentType";
import { INITIAL_VALUES } from "@/pages/configurations/componentTypes/models/FormInitialValues";
import {
	getComponentTypeById,
	updatePartByComponentType,
} from "@/pages/configurations/componentTypes/repositories/ComponentTypes";
import { PartComponentType } from "@/pages/configurations/partComponentTypes/models/PartComponentType";
import { usePartsContext } from "@/pages/configurations/parts/context/PartsContext";
import { DataTable } from "./DataTable/DataTableParts";

type Props = {
	componentTypeId: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const PartsList = ({ componentTypeId, show, onHide }: Props) => {
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [partsSelected, setPartsSelected] = useState([]);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("");
	const [componentType, setComponentType] = useState<ComponentType | null>(
		null,
	);
	const [partComponentTypeForEdit, setPartComponentTypeForEdit] =
		useState<PartComponentType | null>(null);
	const [partsOfComponentType, setPartsOfComponentType] = useState<{
		[partId: number]: PartComponentType;
	}>({});

	const [error, setError] = useState<string | null>(null);

	const {
		state: { parts },
	} = usePartsContext();

	const {
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<PartComponentType>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const onSubmit: SubmitHandler<PartComponentType> = async (data, e) => {
		setIsSubmitLoading(true);
		try {
			const res = await updatePartByComponentType(
				componentTypeId,
				partsSelected,
			);

			if (res?.ok) {
				setMessage("The component type was updated successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage(`Error, please try again`);
			setMessageType("error");

			setError(e as string);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			(async () => {
				setIsLoading(true);
				const part = await getPartByComponentType(componentTypeId);
				const { componentType } = await getComponentTypeById(componentTypeId);
				setComponentType(componentType.result);

				if (part.data?.data?.success) {
					setPartsOfComponentType(part.data?.data?.result);
				} else {
					onHide(true);
					setMessage("Error, part not found. Try again");
					setOpenAlertMessage(true);
					setPartComponentTypeForEdit(null);
				}
				setIsLoading(false);
			})();
		}
	}, [show]);

	useEffect(() => {
		if (show && partComponentTypeForEdit) {
			const { partId, componentTypeId } = partComponentTypeForEdit!;
			setValue("partId", partId);
			setValue("componentTypeId", componentTypeId);
		}
	}, [partComponentTypeForEdit]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="partsTable"
					title={
						componentType?.componentTypeId === componentTypeId
							? `Parts of ${componentType?.componentTypeName}`
							: ""
					}
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "300px" }}
								>
									<CircularProgress size="5rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid item md={12} xs={12}>
										<DataTable
											partsOfComponentType={partsOfComponentType}
											onSelectionChanged={setPartsSelected}
										/>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions style={{ padding: "8px 16px 16px" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
											// setPartForEdit(null)
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>
				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					{messageType == "success" ? (
						<Alert severity="success" onClose={handleClose}>
							{message}
						</Alert>
					) : (
						<Alert severity="error" onClose={handleClose}>
							{message}
						</Alert>
					)}
				</Snackbar>
			</>
			<ErrorModal error={error} onHide={() => setError(null)} />
		</>
	);
};
